import React from "react";
import RichText from "./RichText";

const ListicleTemplate = ({ post }) => (
  <div className="blog-post-layout">
    <aside className="blog-post-sidebar">
      <div className="blog-sidebar-card">
        <h3>In this article</h3>
        <ol>
          {post.sections.map((section, index) => (
            <li key={section.title || index}>
              <a href={`#section-${index + 1}`}>{section.title}</a>
            </li>
          ))}
        </ol>
      </div>

      {post.tags.length > 0 && (
        <div className="blog-sidebar-card">
          <h3>Topics</h3>
          <div className="blog-tag-cloud">
            {post.tags.map((tag) => (
              <span className="blog-tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>

    <article className="blog-post-content">
      {post.sections.map((section, index) => (
        <section
          className="blog-section-card"
          id={`section-${index + 1}`}
          key={section.title || index}
        >
          <div className="blog-section-number">{index + 1}</div>
          <div className="blog-section-body">
            <RichText as="h2" value={section.title} multiline={false} />
            {section.image_url && (
              <img
                src={section.image_url}
                alt={section.title}
                className="blog-section-image"
              />
            )}
            <RichText className="blog-richtext" value={section.content} />
            {section.bullets.length > 0 && (
              <ul className="blog-section-list">
                {section.bullets.map((bullet) => (
                  <li key={bullet}>
                    <RichText as="span" value={bullet} multiline={false} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}
    </article>
  </div>
);

const EditorialTemplate = ({ post }) => (
  <article className="blog-editorial-layout">
    <div className="blog-editorial-intro">
      <div className="blog-editorial-stamp">Editorial Story</div>
      <RichText className="blog-richtext blog-editorial-intro-copy" value={post.excerpt} />
    </div>

    <div className="blog-editorial-stream">
      {post.sections.map((section, index) => (
        <section
          className={`blog-editorial-section ${index % 2 === 1 ? "blog-editorial-section-reverse" : ""}`}
          id={`section-${index + 1}`}
          key={section.title || index}
        >
          <div className="blog-editorial-copy">
            <span className="blog-editorial-step">{String(index + 1).padStart(2, "0")}</span>
            <RichText as="h2" value={section.title} multiline={false} />
            <RichText className="blog-richtext" value={section.content} />
            {section.bullets.length > 0 && (
              <ul className="blog-section-list">
                {section.bullets.map((bullet) => (
                  <li key={bullet}>
                    <RichText as="span" value={bullet} multiline={false} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="blog-editorial-visual">
            {section.image_url ? (
              <img
                src={section.image_url}
                alt={section.title}
                className="blog-editorial-image"
              />
            ) : (
              <div className="blog-editorial-placeholder">
                <span>{post.category}</span>
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  </article>
);

const SpotlightTemplate = ({ post }) => (
  <article className="blog-spotlight-layout">
    {post.tags.length > 0 && (
      <div className="blog-spotlight-topics">
        {post.tags.map((tag) => (
          <span className="blog-tag" key={tag}>
            {tag}
          </span>
        ))}
      </div>
    )}

    <div className="blog-spotlight-grid">
      {post.sections.map((section, index) => (
        <section
          className="blog-spotlight-card"
          id={`section-${index + 1}`}
          key={section.title || index}
        >
          {section.image_url && (
            <img
              src={section.image_url}
              alt={section.title}
              className="blog-spotlight-image"
            />
          )}
          <div className="blog-spotlight-card-body">
            <span className="blog-spotlight-kicker">Spotlight {index + 1}</span>
            <RichText as="h2" value={section.title} multiline={false} />
            <RichText className="blog-richtext" value={section.content} />
            {section.bullets.length > 0 && (
              <div className="blog-spotlight-bullets">
                {section.bullets.map((bullet) => (
                  <div className="blog-spotlight-bullet" key={bullet}>
                    <span className="blog-spotlight-bullet-mark" />
                    <RichText as="span" value={bullet} multiline={false} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  </article>
);

const FrameworkTemplate = ({ post }) => (
  <article className="blog-framework-layout">
    <div className="blog-framework-overview">
      <div className="blog-framework-overview-card">
        <h2>Framework overview</h2>
        <RichText className="blog-richtext" value={post.excerpt} />
      </div>
      <div className="blog-framework-overview-card">
        <h3>Core moves</h3>
        <div className="blog-framework-metrics">
          {post.sections.map((section, index) => (
            <div className="blog-framework-metric" key={section.title || index}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <RichText as="strong" value={section.title} multiline={false} />
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="blog-framework-grid">
      {post.sections.map((section, index) => (
        <section
          className="blog-framework-card"
          id={`section-${index + 1}`}
          key={section.title || index}
        >
          <div className="blog-framework-card-top">
            <div className="blog-framework-number">{index + 1}</div>
            <RichText as="h2" value={section.title} multiline={false} />
          </div>
          {section.image_url && (
            <img
              src={section.image_url}
              alt={section.title}
              className="blog-framework-image"
            />
          )}
          <RichText className="blog-richtext" value={section.content} />
          {section.bullets.length > 0 && (
            <ul className="blog-framework-list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>
                  <RichText as="span" value={bullet} multiline={false} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  </article>
);

const BlogTemplateRenderer = ({ post }) => {
  const template = post.template || "listicle";

  if (template === "editorial") {
    return <EditorialTemplate post={post} />;
  }

  if (template === "spotlight") {
    return <SpotlightTemplate post={post} />;
  }

  if (template === "framework") {
    return <FrameworkTemplate post={post} />;
  }

  return <ListicleTemplate post={post} />;
};

export default BlogTemplateRenderer;
